import React, { useState, useEffect } from 'react';
import {
    Search,
    Bell,
    Menu,
    UserCheck,
    LogOut,
    ChevronLeft,
    X
} from 'lucide-react';
import { adminApiClient } from '../../lib/api';
import { getAssetUrl } from '../../lib/utils';

interface HeaderProps {
    sidebarCollapsed: boolean;
    setSidebarCollapsed: (collapsed: boolean) => void;
    user: any;
    logout: () => void;
    setActiveTab: (tab: string) => void;
    config: any;
}

interface SearchResult {
    id: string;
    type: 'user' | 'temple';
    title: string;
    subtitle: string;
}

interface NotificationItem {
    id: string;
    type: 'donation' | 'user' | 'temple';
    message: string;
    time: string;
    date: Date;
    rawDate: string;
}

export const Header: React.FC<HeaderProps> = ({
    sidebarCollapsed,
    setSidebarCollapsed,
    user,
    logout,
    setActiveTab,
    config
}) => {
    // Search State
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const [showSearchResults, setShowSearchResults] = useState(false);

    // Notification State
    const [notifications, setNotifications] = useState<NotificationItem[]>([]);
    const [showNotifications, setShowNotifications] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);

    // Initial Data Load for Search & Notifications
    // In a real app, you might want to fetch this globally or use a context
    // Here we'll fetch on mount to populate notifications and setup search data
    const [allUsers, setAllUsers] = useState<any[]>([]);
    const [allTemples, setAllTemples] = useState<any[]>([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [usersRes, templesRes, donationsRes] = await Promise.all([
                    adminApiClient.getUsers(),
                    adminApiClient.getTemples(),
                    adminApiClient.getDonations()
                ]);

                const users = (usersRes as any).users || [];
                const temples = (templesRes as any).temples || [];
                const donations = (donationsRes as any).donations || [];

                setAllUsers(users);
                setAllTemples(temples);

                // Process Notifications (Recent Activities)
                const activities: NotificationItem[] = [];

                // Add recent donations
                donations.slice(0, 5).forEach((d: any) => {
                    activities.push({
                        id: `don-${d.id}`,
                        type: 'donation',
                        message: `Donation: $${d.amount} from ${d.donorName || 'Anonymous'}`,
                        time: new Date(d.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                        date: new Date(d.createdAt),
                        rawDate: d.createdAt
                    });
                });

                // Add recent users
                users.slice(0, 5).forEach((u: any) => {
                    activities.push({
                        id: `usr-${u.id}`,
                        type: 'user',
                        message: `New User: ${u.name} (${u.role})`,
                        time: new Date(u.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                        date: new Date(u.createdAt),
                        rawDate: u.createdAt
                    });
                });

                // Add recent temples
                temples.slice(0, 5).forEach((t: any) => {
                    activities.push({
                        id: `tmp-${t.id}`,
                        type: 'temple',
                        message: `New Temple: ${t.name}`,
                        time: new Date(t.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                        date: new Date(t.createdAt),
                        rawDate: t.createdAt
                    });
                });

                // Sort by date desc
                activities.sort((a, b) => b.date.getTime() - a.date.getTime());
                setNotifications(activities.slice(0, 8)); // Keep top 8
                setUnreadCount(3); // Mock unread count

            } catch (error) {
                console.error("Error fetching header data:", error);
            }
        };

        fetchData();
    }, []);

    // Handle Search
    useEffect(() => {
        if (!searchQuery.trim()) {
            setSearchResults([]);
            setShowSearchResults(false);
            return;
        }

        const timer = setTimeout(() => {
            const query = searchQuery.toLowerCase();

            const results: SearchResult[] = [];

            // Search Users
            allUsers.forEach((u: any) => {
                if (u.name?.toLowerCase().includes(query) || u.email?.toLowerCase().includes(query)) {
                    results.push({
                        id: u.id,
                        type: 'user',
                        title: u.name,
                        subtitle: u.role
                    });
                }
            });

            // Search Temples
            allTemples.forEach((t: any) => {
                if (t.name?.toLowerCase().includes(query) || t.location?.toLowerCase().includes(query)) {
                    results.push({
                        id: t.id,
                        type: 'temple',
                        title: t.name,
                        subtitle: t.location || 'No location'
                    });
                }
            });

            setSearchResults(results.slice(0, 5)); // Limit to 5 results
            setShowSearchResults(true);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchQuery, allUsers, allTemples]);

    const handleSearchResultClick = (result: SearchResult) => {
        if (result.type === 'user') {
            setActiveTab('users');
        } else if (result.type === 'temple') {
            setActiveTab('temples');
        }
        setSearchQuery('');
        setShowSearchResults(false);
    };

    const handleNotificationClick = (item: NotificationItem) => {
        if (item.type === 'user') setActiveTab('users');
        else if (item.type === 'temple') setActiveTab('temples');
        else if (item.type === 'donation') setActiveTab('donations');
        setShowNotifications(false);
    };

    const getImageUrl = (path: string) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        return getAssetUrl(path);
    };

    return (
        <header className="h-16 bg-slate-900 border-b border-slate-800 sticky top-0 z-30 px-6 flex items-center justify-between shadow-sm">
            {/* Mobile Handlers */}
            <div className="md:hidden flex items-center gap-4">
                <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} className="p-2 -ml-2 text-slate-300 hover:text-white">
                    <Menu size={24} />
                </button>
                {/* Mobile Logo */}
                <div className="flex items-center gap-2">
                    {config?.logo ? (
                        <img src={getImageUrl(config.logo)} alt="Logo" className="h-8 w-auto" />
                    ) : (
                        <span>🕉️</span>
                    )}
                    <span className="font-bold text-white">FundDharma</span>
                </div>
            </div>

            {/* Left spacer for Desktop */}
            <div className="hidden md:block"></div>

            <div className="header-right flex items-center gap-4">
                {/* Global Search */}
                <div className="relative hidden md:block">
                    <div className="flex items-center bg-slate-800 px-3 py-2 rounded-lg border border-slate-700 w-64 focus-within:border-[var(--color-primary)] focus-within:w-80 transition-all duration-300">
                        <Search size={18} className="text-slate-400 mr-2" />
                        <input
                            type="text"
                            placeholder="Search users, temples..."
                            className="bg-transparent border-none outline-none text-sm w-full text-white placeholder-slate-500"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onFocus={() => { if (searchQuery) setShowSearchResults(true); }}
                        />
                        {searchQuery && (
                            <button onClick={() => setSearchQuery('')} className="text-slate-500 hover:text-white">
                                <X size={14} />
                            </button>
                        )}
                    </div>

                    {/* Search Dropdown */}
                    {showSearchResults && (
                        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50 overflow-hidden">
                            {searchResults.length > 0 ? (
                                <>
                                    <p className="px-4 py-1 text-xs font-semibold text-gray-400 uppercase">Results</p>
                                    {searchResults.map(result => (
                                        <button
                                            key={result.id}
                                            onClick={() => handleSearchResultClick(result)}
                                            className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center justify-between group"
                                        >
                                            <div>
                                                <p className="text-sm font-medium text-gray-800 group-hover:text-[var(--color-tertiary)]">{result.title}</p>
                                                <p className="text-xs text-gray-500 capitalize">{result.type} • {result.subtitle}</p>
                                            </div>
                                            <ChevronLeft size={16} className="text-gray-300 rotate-180 group-hover:text-[var(--color-primary)]" />
                                        </button>
                                    ))}
                                </>
                            ) : (
                                <p className="px-4 py-3 text-sm text-gray-500 text-center">No results found.</p>
                            )}
                        </div>
                    )}
                </div>

                {/* Notifications */}
                <div className="notifications relative">
                    <button
                        className={`p-2 rounded-full transition-colors relative ${showNotifications ? 'bg-slate-800 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'}`}
                        onClick={() => setShowNotifications(!showNotifications)}
                    >
                        <Bell size={20} />
                        {unreadCount > 0 && (
                            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border-2 border-slate-900 animate-pulse"></span>
                        )}
                    </button>

                    {/* Notification Dropdown */}
                    {showNotifications && (
                        <>
                            {/* Backdrop to close */}
                            <div className="fixed inset-0 z-40" onClick={() => setShowNotifications(false)}></div>
                            <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-100 py-0 z-50 overflow-hidden">
                                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                                    <h3 className="font-semibold text-gray-800">Notifications</h3>
                                    <button
                                        onClick={() => setUnreadCount(0)}
                                        className="text-xs text-[var(--color-primary)] hover:opacity-80 font-medium"
                                    >
                                        Mark all read
                                    </button>
                                </div>
                                <div className="max-h-96 overflow-y-auto">
                                    {notifications.length > 0 ? (
                                        notifications.map((item) => (
                                            <button
                                                key={item.id}
                                                onClick={() => handleNotificationClick(item)}
                                                className="w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-[color-mix(in_srgb,var(--color-primary)_10%,white)] transition-colors flex gap-3 group"
                                            >
                                                <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${item.type === 'donation' ? 'bg-green-500' : item.type === 'temple' ? 'bg-[var(--color-primary)]' : 'bg-blue-500'}`}></div>
                                                <div>
                                                    <p className="text-sm text-gray-800 leading-snug group-hover:text-[var(--color-tertiary)]">{item.message}</p>
                                                    <p className="text-xs text-gray-400 mt-1">{item.time}</p>
                                                </div>
                                            </button>
                                        ))
                                    ) : (
                                        <div className="px-4 py-8 text-center text-gray-500">
                                            <p>No new notifications</p>
                                        </div>
                                    )}
                                </div>
                                <div className="bg-gray-50 px-4 py-2 text-center border-t border-gray-100">
                                    <button onClick={() => { setActiveTab('dashboard'); setShowNotifications(false); }} className="text-xs font-semibold text-gray-600 hover:text-gray-900">
                                        View All Activity
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <div className="h-8 w-px bg-slate-700 mx-2"></div>

                {/* User Profile */}
                <div className="user-profile flex items-center gap-3 pl-2">
                    <div className="h-9 w-9 rounded-full flex items-center justify-center text-white font-bold border-2 border-slate-700 shadow-sm bg-[linear-gradient(135deg,var(--color-primary),var(--color-tertiary))]">
                        {user?.name?.charAt(0)?.toUpperCase() || 'A'}
                    </div>
                    <div className="hidden sm:block text-left">
                        <p className="text-sm font-semibold text-white leading-none">{user?.name || 'Admin User'}</p>
                        <p className="text-xs text-slate-400 mt-1">{user?.role?.replace('_', ' ').toUpperCase() || 'ADMIN'}</p>
                    </div>
                    <div className="user-menu relative group">
                        <button className="p-1 text-slate-400 hover:text-white">
                            <ChevronLeft size={16} className="rotate-270" />
                        </button>
                        <div className="absolute right-0 top-full w-48 pt-2 hidden group-hover:block z-50">
                            <div className="bg-white rounded-xl shadow-xl border border-gray-100 py-1 overflow-hidden">
                                <div className="px-4 py-2 border-b border-gray-100 md:hidden">
                                    <p className="text-sm font-semibold text-gray-900">{user?.name}</p>
                                    <p className="text-xs text-gray-500">{user?.role}</p>
                                </div>
                                <button
                                    onClick={() => setActiveTab('profile')}
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                >
                                    <UserCheck size={16} /> Profile
                                </button>
                                <button onClick={logout} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                                    <LogOut size={16} /> Logout
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
};
